import getDisplayableItemCount from "./getDisplayableItemCount";
import getSavedItems from "./getSavedItems";
import { isPlaceholder } from "./itemStateSelectors";
import { THRESHOLD_LIMIT } from "../constants";

const getMidIndexOfBlock = (start, end) =>
  start + Math.floor((end - start) / 2);

const getUnobservedBlockStart = ({ block, previousBlock, totalItems }) => {
  const lastItemIndex = totalItems - 1;
  const bufferedIndexAfterPreviousBlock =
    previousBlock.end + THRESHOLD_LIMIT + 1;
  const middleOfThisBlockIndex = getMidIndexOfBlock(block.start, block.end);

  return Math.min(
    lastItemIndex,
    bufferedIndexAfterPreviousBlock,
    middleOfThisBlockIndex
  );
};

const getObservedBlockStart = ({ block, previousBlock }) => {
  const bufferedIndexBeforeThisBlock = block.start - THRESHOLD_LIMIT;
  const middleOfPreviousBlockIndex =
    getMidIndexOfBlock(previousBlock.start, previousBlock.end) + 1;

  return Math.max(0, bufferedIndexBeforeThisBlock, middleOfPreviousBlockIndex);
};

const getUnobservedBlockEnd = ({ block, nextBlock }) => {
  const bufferedIndexBeforeNextBlock = nextBlock.start - THRESHOLD_LIMIT - 1;
  const middleOfThisBlockIndex = getMidIndexOfBlock(block.start, block.end);

  return Math.max(0, bufferedIndexBeforeNextBlock, middleOfThisBlockIndex);
};

const getObservedBlockEnd = ({ block, nextBlock, lastItemIndex }) => {
  const bufferedIndexAfterThisBlock = block.end + THRESHOLD_LIMIT;
  const middleOfNextBlockIndex =
    getMidIndexOfBlock(nextBlock.start, nextBlock.end) - 1;

  return Math.min(
    lastItemIndex,
    bufferedIndexAfterThisBlock,
    middleOfNextBlockIndex
  );
};

const getBufferedStart = ({ block, totalItems, index, itemBlocks }) => {
  if (block.start === 0) return 0;

  const previousBlock = itemBlocks[index - 1];

  return block.observed
    ? getObservedBlockStart({ block, previousBlock })
    : getUnobservedBlockStart({ block, previousBlock, totalItems });
};

const getBufferedEnd = ({ block, totalItems, index, itemBlocks }) => {
  const lastItemIndex = totalItems - 1;

  if (block.end === lastItemIndex) return block.end;

  const nextBlock = itemBlocks[index + 1];

  return block.observed
    ? getObservedBlockEnd({ block, nextBlock, lastItemIndex })
    : getUnobservedBlockEnd({ block, nextBlock });
};

const toItemBlocks = (blocks, item, index) => {
  const numBlocks = blocks.length;
  const lastBlockIndex = numBlocks - 1;
  const observed = isPlaceholder(item);
  const isNewBlock =
    numBlocks === 0 || observed !== blocks[lastBlockIndex].observed;

  return isNewBlock
    ? [
        ...blocks,
        {
          observed,
          start: index,
          end: index
        }
      ]
    : [
        ...blocks.slice(0, lastBlockIndex),
        {
          ...blocks[lastBlockIndex],
          end: index
        }
      ];
};

export default state => {
  const totalItems = getDisplayableItemCount(state);

  return getSavedItems(state)
    .reduce(toItemBlocks, [])
    .map((block, index, itemBlocks) => ({
      ...block,
      bufferedStart: getBufferedStart({ block, totalItems, index, itemBlocks }),
      bufferedEnd: getBufferedEnd({ block, totalItems, index, itemBlocks })
    }));
};
